// Trade-specific landing-page content. Shared between the worker (SEO head +
// sitemap in seo.ts) and the React TradePage component, so there's a single
// source of truth and no duplicated slug lists.
//
// Each page targets a high-intent keyword like "{trade} CRM software". Pain
// points, hero copy, and FAQs are written distinctly per trade (that's the
// content Google + the visitor actually weigh); the product feature list is
// genuinely shared because it's the same product.

export interface TradeFaq {
  q: string;
  a: string;
}

// Deeper sections (optional — only populated for the top-5 trades by
// human traffic). Rendered by TradePage.tsx only when present, so
// under-invested trades stay lean until they earn the extra content.
export interface TradeWorkflowStep {
  stage: string;  // "Lead", "Quote", "Schedule", "Complete", "Invoice"
  what: string;   // one-sentence description of the step in this trade's context
}
export interface TradeService {
  name: string;
  detail: string;
  range?: string;  // typical ticket range, e.g. "$8k-25k"
}

export interface Trade {
  slug: string; // page lives at /{slug}-crm
  name: string; // "HVAC"
  noun: string; // "HVAC contractors"
  seoTitle: string;
  seoDescription: string;
  heroTitle: string;
  heroSub: string;
  intro: string;
  pains: { title: string; body: string }[];
  faqs: TradeFaq[];
  // Deeper content — top 5 trades only
  workflow?: TradeWorkflowStep[];
  services?: TradeService[];
  seasonalTips?: string[];
}

export const TRADES: Trade[] = [
  {
    slug: "hvac",
    name: "HVAC",
    noun: "HVAC contractors",
    seoTitle: "HVAC CRM Software — Quotes, Scheduling & Payments",
    seoDescription:
      "A CRM built for HVAC contractors: track install and service leads, send quotes, schedule the job and take card payments from your phone.",
    heroTitle: "CRM software built for HVAC contractors",
    heroSub:
      "Track install and service calls, quote on the spot, schedule your techs, and get paid — all from your phone.",
    intro:
      "Between maintenance contracts, emergency no-heat calls, and install bids, an HVAC business runs on tight scheduling and fast quoting. FieldKrew keeps every lead, job, and payment in one place so a service call never falls through the cracks and a $12,000 system bid never sits unanswered.",
    pains: [
      {
        title: "Service calls slip through the cracks",
        body: "A no-cool call comes in while you're on a roof; by the time you remember, they've called the next company in the search results.",
      },
      {
        title: "Maintenance contracts go unbilled",
        body: "Seasonal tune-ups are recurring revenue — but only if someone remembers to schedule and invoice them.",
      },
      {
        title: "Install bids sit in a text thread",
        body: "A five-figure system replacement deserves a real quote and a follow-up, not a number fired off in a text.",
      },
    ],
    faqs: [
      {
        q: "Does FieldKrew work for both HVAC service and installation?",
        a: "Yes. Track quick service calls and large install bids in the same pipeline, quote either from your phone, and schedule techs on a shared calendar.",
      },
      {
        q: "Can I set up recurring maintenance jobs?",
        a: "Yes — recurring jobs auto-generate on the schedule you set (e.g. spring and fall tune-ups) so seasonal maintenance revenue never gets forgotten.",
      },
      {
        q: "Can my customers pay by card?",
        a: "Yes. Send a pay link by text or email and take Visa, Mastercard, Amex, Apple Pay, and Google Pay. FieldKrew adds 0% platform fee for Pro accounts (1% for Free) on top of Stripe's standard 2.9% + $0.30 card processing.",
      },
      {
        q: "How much does HVAC CRM software cost?",
        a: "FieldKrew is free forever for solo contractors with unlimited leads, jobs, quotes, and invoices — then $49.99/month for crew + photos + automation — a fraction of Jobber or Housecall Pro, with no per-tech fees.",
      },
      {
        q: "How does FieldKrew compare to Jobber for HVAC?",
        a: "Jobber Core is $29-49/mo (1 user, $29 per extra) — most HVAC shops need Grow ($199-399/mo) for the recurring maintenance features. FieldKrew is $49.99/mo flat with 10 users and recurring built in from the start. An HVAC shop with 3 techs pays roughly 4-6x more on Jobber for equivalent capability.",
      },
      {
        q: "How does FieldKrew compare to Housecall Pro for HVAC?",
        a: "HCP Essentials is $149-189/mo for 5 users; Max is $299-329/mo for 8. FieldKrew is $49.99/mo flat for 10. HCP is more mature and polished; FieldKrew is newer, cheaper, and modern-stack. Both work for HVAC; the choice comes down to whether you value polish + support (HCP) or price + speed of updates (FieldKrew).",
      },
      {
        q: "How does FieldKrew compare to ServiceTitan for HVAC?",
        a: "ServiceTitan is enterprise HVAC/plumbing/electrical, typically $250-500 per technician per month with a 12-month contract and $5-50k implementation. FieldKrew is $49.99/mo flat with no contract or implementation fee. ServiceTitan wins for 20+ tech shops with a dispatcher; FieldKrew wins for 1-10 tech shops that want to try before buying.",
      },
      {
        q: "How does FieldKrew compare to FieldEdge or Housecall Pro for HVAC?",
        a: "FieldEdge is HVAC-specific at ~$100-200/mo per user with strong dispatching. FieldKrew is $49.99/mo flat for 10 users; simpler dispatching, cheaper, faster to set up. Good for smaller shops; scale beyond a dispatcher and you want FieldEdge or ServiceTitan.",
      },
      {
        q: "Can I import my customer list from Housecall Pro or Jobber?",
        a: "Yes. CSV export → CSV upload; FieldKrew maps customers, equipment history, service history, and recurring maintenance contracts. A 500-customer HCP list imports in ~15 minutes.",
      },
      {
        q: "Does FieldKrew track equipment installed at each property (make, model, install date, warranty)?",
        a: "Yes. Each customer has an Equipment section — HVAC units, furnaces, water heaters, thermostats — with make, model, serial, install date, warranty expiry. Populates into repair quotes so you're not asking the customer what unit they have.",
      },
      {
        q: "Does FieldKrew handle recurring maintenance agreements (silver / gold / platinum)?",
        a: "Yes. Set customers on tiered plans; recurring jobs auto-spawn on schedule (spring + fall tune-ups) with the plan's pricing. Auto-invoices on completion. Monthly billing option for plan revenue.",
      },
      {
        q: "Can I offer online booking for AC diagnostic calls?",
        a: "Yes. Public Booking page — customer picks service type (no-cool / no-heat / tune-up / new-system estimate), preferred date, pays trip-charge deposit. Auto-scheduled to your calendar.",
      },
      {
        q: "Does FieldKrew sync with QuickBooks for HVAC?",
        a: "Yes. Customers, invoices, payments both directions. Handles class tracking (service vs install vs maintenance-plan revenue) and 1099 sync for subcontracted install crews.",
      },
      {
        q: "How does dispatch work when a no-cool call comes in mid-day?",
        a: "New job hits the shared calendar; drag-drop assign to whichever tech is closest (crew-location view shows real-time positions on Pro). Tech gets an SMS with the job link. Customer gets an automatic ETA text.",
      },
      {
        q: "Does FieldKrew handle install permits and inspection scheduling?",
        a: "Not natively — no jurisdiction API integrations. Custom Fields let you track permit numbers, submit dates, and inspection dates per job with reminders. Manual but organized.",
      },
      {
        q: "Can I quote a new AC/furnace system on the customer's kitchen table?",
        a: "Yes. Build the quote on your phone or tablet from a saved price book (specific units with pre-loaded prices); customer signs on the same device. Auto-emailed to them within seconds of signing.",
      },
      {
        q: "How do I request Google reviews after HVAC service?",
        a: "Reviews module sends SMS/email 24-48 hours after job completion with one-tap link. HVAC review response rates typically 30-50%.",
      },
      {
        q: "Is there a mobile app for HVAC techs?",
        a: "Native iOS on the App Store; Android in beta (Play Store Q4 2026). Techs quote on the spot, log parts installed, take photos, mark complete, invoice — all from the truck.",
      },
      {
        q: "Does FieldKrew work offline in basements without signal?",
        a: "iOS app queues quotes, photos, and job notes when offline; syncs when signal returns. Android and web are network-dependent.",
      },
      {
        q: "How long does it take to switch from FieldEdge or Housecall Pro?",
        a: "Most HVAC shops migrate in a weekend: CSV export Friday, upload + spot-check Saturday, cut over Monday. Historical jobs come as read-only; new jobs run through FieldKrew.",
      },
      {
        q: "Is my data safe? Can I export everything if I leave?",
        a: "One-click CSV export of every customer, equipment record, maintenance plan, and invoice. Daily backups to Cloudflare R2, 30-day retention. No lock-in.",
      },
    ],
    workflow: [
      { stage: "Service call intake", what: "Homeowner calls with a no-cool complaint; auto-creates a lead with customer address + equipment history (if repeat) already populated. Trip-charge quote sent by SMS instantly." },
      { stage: "Dispatch", what: "Job hits the shared calendar. Drag-drop to the closest available tech (crew-location view on Pro shows real-time positions). Customer gets automatic ETA text." },
      { stage: "On-site diagnostic", what: "Tech opens the job on their phone, sees equipment history (last capacitor replacement was 8 months ago), takes photos of the failed part, records reading." },
      { stage: "Quote + approve", what: "Build repair or replacement quote from saved price book on the customer's kitchen table. E-sign on the phone; deposit collected via link if it's a full-system install." },
      { stage: "Parts + install", what: "For repair: tech has parts in truck, invoice on the spot. For install: order equipment against the job (make/model tagged), schedule crew day, deposit + progress-billing." },
      { stage: "Final invoice", what: "Job complete → invoice generates with parts + labor + any applicable warranty registration → sent by SMS/email with pay link → autopay collects." },
      { stage: "Warranty + recall", what: "Installed equipment auto-registered against the customer with warranty expiry dates. When a manufacturer recalls a specific model, filter customers to see who's affected." },
      { stage: "Recurring maintenance", what: "Customer opted into silver/gold/platinum plan? Recurring engine spawns spring + fall tune-ups on schedule with the plan's pricing. Auto-invoices monthly for plan revenue." },
    ],
    services: [
      { name: "Trip / diagnostic charge", detail: "Standard fee for showing up + diagnosing; credited toward the repair if customer proceeds. Base of every service call.", range: "$85-175" },
      { name: "Capacitor / contactor replacement", detail: "Common summer no-cool fix. 15-30 min labor + $20-60 part. High-margin quick work.", range: "$250-450" },
      { name: "Refrigerant top-off (R-410A)", detail: "Leak-check first (repair the leak or you're throwing money away); then charge to spec by weight. Price varies wildly by refrigerant cost.", range: "$300-800" },
      { name: "Blower motor replacement", detail: "OEM or aftermarket ECM motor + labor. 1-2 hour job.", range: "$500-1,200" },
      { name: "Full AC system install (3-ton residential)", detail: "Outdoor condenser + indoor coil + line set + refrigerant. Permit + inspection typically required.", range: "$5,500-9,000" },
      { name: "Full HVAC system install (heat pump / gas furnace + AC)", detail: "Complete indoor + outdoor equipment swap, ductwork check, thermostat install.", range: "$8,000-16,000" },
      { name: "Furnace replacement", detail: "80% or 96% AFUE gas furnace, includes venting adjustments if efficiency change.", range: "$4,500-8,500" },
      { name: "Heat pump install", detail: "Increasingly popular with IRA tax credits; standard or cold-climate variant.", range: "$7,500-14,000" },
      { name: "Ductwork repair or replacement", detail: "Duct sealing (Aeroseal or manual), replace collapsed sections, add returns. Often bundled with system install.", range: "$500-4,500" },
      { name: "Silver maintenance plan (annual)", detail: "One tune-up per year + priority scheduling + parts discount. Predictable recurring revenue.", range: "$120-200/yr" },
      { name: "Gold / platinum maintenance plan", detail: "Two tune-ups (spring + fall) + full parts warranty + priority + no trip charges. Higher-margin annual contracts.", range: "$250-500/yr" },
    ],
    seasonalTips: [
      "Cooling season (May-September in most of the US) is your peak revenue window; heating season (November-March) is your #2. Shoulder months (April, October) are when you push maintenance-plan enrollments to smooth revenue year-round.",
      "The best time to sell a full-system install is when a customer's 15-year-old unit fails in July — they can't wait 3 weeks for quotes. Fast response + on-the-spot financing (integrate with GreenSky, Wisetack, or Sunlight) closes at 60-80% vs 20-30% for cold quotes.",
      "IRA (Inflation Reduction Act) heat-pump tax credits and utility rebates run through 2032 — becoming fluent in your state's rebate stacking (federal + state + utility) is a sales tool. Some customers will let a $3k rebate be the tie-breaker on which contractor they pick.",
      "Maintenance plans are the entire recurring-revenue play in HVAC — a 500-customer maintenance base at $180/yr is $90k annual revenue that shows up regardless of weather. Auto-invoicing (kills the 'they forgot to renew' issue) is the single highest-leverage feature for a HVAC ops.",
      "Refrigerant law changes (R-22 phase-out complete, R-410A phasing down 2025-2036, R-454B and R-32 replacing) mean equipment recommendations shift year-over-year — keeping your team's certifications current + your price book updated is the operational hygiene that separates growing shops from stagnant ones.",
    ],
  },
  {
    slug: "plumbing",
    name: "Plumbing",
    noun: "plumbers",
    seoTitle: "Plumbing CRM Software — Quotes, Jobs & Invoices",
    seoDescription:
      "A CRM built for plumbers: capture the call, quote on site, schedule the job and invoice before you leave the driveway. Free plan, no card.",
    heroTitle: "CRM software built for plumbers",
    heroSub:
      "From emergency calls to repipes — capture the job, quote it fast, document it with photos, and collect payment before you leave.",
    intro:
      "Plumbing is half emergencies, half planned work, and all about responding fast. FieldKrew gives plumbers one place to take the call, quote the repair, photo-document the before/after for the customer, and collect payment on the spot — so you're never chasing a check or losing an urgent job to a slow callback.",
    pains: [
      {
        title: "Emergency calls don't wait",
        body: "A burst pipe customer calls three plumbers. The one who responds and quotes first wins — speed is the whole game.",
      },
      {
        title: "No proof of the before/after",
        body: "When a customer questions the bill, photos of the corroded pipe you replaced end the conversation fast.",
      },
      {
        title: "Chasing payment after the job",
        body: "You fixed it, they said 'I'll mail a check,' and now you're calling them three weeks later.",
      },
    ],
    faqs: [
      {
        q: "Is FieldKrew good for a one-person plumbing business?",
        a: "Yes — it's built for solo plumbers and small crews. Start free, and everything runs from your phone in the field.",
      },
      {
        q: "Can I take payment at the customer's house?",
        a: "Yes. Generate a pay link on site and the customer pays by card or digital wallet immediately — no waiting on a mailed check.",
      },
      {
        q: "Can I store photos against each job?",
        a: "Yes. Snap before/during/after photos that stay attached to the job and can be shared with the customer in a branded gallery.",
      },
      {
        q: "How does the price compare to other plumbing software?",
        a: "FieldKrew is free to start and $49.99/month unlimited — versus $49+ for Jobber or $65+ for Housecall Pro, neither with per-user pricing as low.",
      },
    ],
  },
  {
    slug: "electrician",
    name: "Electrical",
    noun: "electricians",
    seoTitle: "Electrician CRM Software — Quotes, Jobs & Payments",
    seoDescription:
      "A CRM built for electricians: track service calls and panel jobs, quote line by line, document the work with photos and get paid on completion.",
    heroTitle: "CRM software built for electricians",
    heroSub:
      "Track every lead, build clean estimates, stay on top of permits and inspections, and collect payment online.",
    intro:
      "Panel upgrades, EV chargers, service calls, remodels — electrical work mixes quick jobs with permit-heavy projects. FieldKrew keeps the estimate, the schedule, the permit status, and the photo documentation together so nothing stalls between bid and final inspection.",
    pains: [
      {
        title: "Permit and inspection status lives in your head",
        body: "Which job is waiting on a rough-in inspection? Miss it and the whole project sits idle.",
      },
      {
        title: "Estimates take too long to send",
        body: "By the time you've typed up the panel-upgrade quote at night, the homeowner's gotten two faster bids.",
      },
      {
        title: "Photo documentation for code compliance",
        body: "You need the before/after on file — for the customer, the inspector, and your own protection.",
      },
    ],
    faqs: [
      {
        q: "Can FieldKrew track permit and inspection stages?",
        a: "Yes — the job pipeline includes a 'waiting on permit' stage, and per-job checklists let you track rough-in, final, and inspection steps.",
      },
      {
        q: "Can I build estimates with line items?",
        a: "Yes. Build itemized estimates from a saved price book, save reusable templates, and send a branded PDF the customer can accept online.",
      },
      {
        q: "Does it work on the job site?",
        a: "FieldKrew is mobile-first and works offline, so you can quote and document from a basement or a new-construction site with no signal.",
      },
      {
        q: "What does electrician CRM software cost?",
        a: "Free for small volume, then $49.99/month for unlimited leads, jobs, estimates, and photos — no per-electrician fees.",
      },
    ],
  },
  {
    slug: "painting",
    name: "Painting",
    noun: "painting contractors",
    seoTitle: "Painting CRM Software — Estimates, Jobs & Invoices",
    seoDescription:
      "A CRM built for painting contractors: estimate by the room or the square foot, schedule the crew, document before and after, invoice on finish.",
    heroTitle: "CRM software built for painting contractors",
    heroSub:
      "Quote interior and exterior jobs fast, schedule your crews, show off before/after work, and collect deposits online.",
    intro:
      "Painting is a high-volume, high-follow-up business — lots of estimates, lots of 'let me think about it,' and lots of crews to keep moving. FieldKrew helps painters quote quickly, follow up automatically before the lead goes cold, and prove the quality of finished work with shareable before/after galleries.",
    pains: [
      {
        title: "Estimates go cold without follow-up",
        body: "Painting customers shop around. Without a nudge a few days later, a winnable job quietly goes to someone else.",
      },
      {
        title: "Before/after is your best sales tool — if you can find it",
        body: "Gorgeous finished work buried in your camera roll does nothing. On a job, it sells the next one.",
      },
      {
        title: "Deposits protect you from no-shows",
        body: "Booking a crew for a week on a handshake is a gamble. A deposit makes it real.",
      },
    ],
    faqs: [
      {
        q: "Does FieldKrew remind me to follow up on quotes?",
        a: "Yes — quotes get auto-flagged when they've been sitting a few days so you can follow up with one tap, which is where most painting jobs are won or lost.",
      },
      {
        q: "Can I collect a deposit before starting?",
        a: "Yes. Bill a deposit (a percent or flat amount), then a progress or final payment — all by card, straight to your bank.",
      },
      {
        q: "Can I show customers before/after photos?",
        a: "Yes. Build a branded before/after gallery for each job and share it with a link — great for closing and for reviews.",
      },
      {
        q: "How much is painting CRM software?",
        a: "Free to start, $49.99/month for unlimited — far less than Jobber or Housecall Pro, with no per-painter fees.",
      },
    ],
  },
  {
    slug: "landscaping",
    name: "Landscaping",
    noun: "landscapers",
    seoTitle: "Landscaping CRM — Quotes, Recurring Jobs, Invoices",
    seoDescription:
      "A CRM built for landscapers: quote installs and maintenance, run recurring routes, track the crew and bill monthly without the spreadsheet.",
    heroTitle: "CRM software built for landscapers",
    heroSub:
      "Quote installs and design jobs, schedule crews, automate recurring maintenance, and get paid on time.",
    intro:
      "Landscaping blends big one-off installs with steady recurring maintenance — and both need tight scheduling and reliable billing. FieldKrew keeps project bids and recurring lawn-care routes in one place, auto-generates repeat jobs, and makes collecting payment as easy as sending a link.",
    pains: [
      {
        title: "Recurring maintenance is a scheduling headache",
        body: "Weekly and biweekly routes are your bread and butter, but tracking them on paper is a mess.",
      },
      {
        title: "Big install bids need real quotes",
        body: "A patio or full-yard redesign is a serious number — it deserves a professional quote, not a scribbled estimate.",
      },
      {
        title: "Seasonal cash flow swings",
        body: "Spring slams you, winter dries up. Recurring billing smooths the ride.",
      },
    ],
    faqs: [
      {
        q: "Can FieldKrew handle recurring lawn-care jobs?",
        a: "Yes — set up recurring jobs (weekly, biweekly, monthly) that auto-generate on schedule so maintenance routes run themselves.",
      },
      {
        q: "Can I quote large landscaping installs?",
        a: "Yes. Build itemized quotes from a saved price list, send a branded PDF, and collect a deposit before the crew starts.",
      },
      {
        q: "Does it help with seasonal customer outreach?",
        a: "Yes — FieldKrew can resurface past customers for seasonal check-ins so you fill the schedule before the busy season starts.",
      },
      {
        q: "What does landscaping CRM software cost?",
        a: "Free for low volume, then $49.99/month unlimited — no per-crew-member fees.",
      },
    ],
  },
  {
    slug: "general-contractor",
    name: "General contracting",
    noun: "general contractors",
    seoTitle: "General Contractor CRM — Quotes, Jobs, Subs",
    seoDescription:
      "A CRM built for general contractors: bid the job, manage subs and change orders, track progress billing and keep the paperwork in one place.",
    heroTitle: "CRM software built for general contractors",
    heroSub:
      "Manage bids, run jobs from start to punch list, coordinate subs, handle change orders, and bill in stages.",
    intro:
      "General contracting is project management on hard mode — multiple subs, change orders, permits, draws, and a punch list at the end. FieldKrew keeps the whole project organized: bid to schedule to subcontractor coordination to staged billing, with photo documentation and signed change orders so nothing's disputed later.",
    pains: [
      {
        title: "Coordinating subs is a full-time job",
        body: "Who's confirmed for Tuesday? Is their insurance current? Tracking it across texts is chaos.",
      },
      {
        title: "Change orders cause disputes",
        body: "'I never approved that' — without a signed change order, the upcharge becomes an argument.",
      },
      {
        title: "Progress billing keeps cash flowing",
        body: "On a long project you can't wait until the end to get paid. Draws keep the lights on.",
      },
    ],
    faqs: [
      {
        q: "Can FieldKrew manage subcontractors?",
        a: "Yes — track subs by trade, assign them to jobs, monitor certificate-of-insurance expiry, and manage 1099 payments at year end.",
      },
      {
        q: "Does it handle change orders?",
        a: "Yes. Send a change order with a price delta; the customer approves it with a signature online, and it adds to the job total automatically.",
      },
      {
        q: "Can I bill in progress draws?",
        a: "Yes — invoice a deposit, progress draws, and a final balance against the job total, all by card or with low-cost options for big balances.",
      },
      {
        q: "How much does GC software cost?",
        a: "Free to start, $49.99/month for unlimited jobs, subs, and change orders — a fraction of enterprise field-service software.",
      },
    ],
  },
  {
    slug: "handyman",
    name: "Handyman",
    noun: "handyman businesses",
    seoTitle: "Handyman CRM Software — Quotes, Jobs & Payments",
    seoDescription:
      "A CRM built for handymen: capture the lead, quote fast, schedule the visit and take payment on site. Free plan, no credit card to start.",
    heroTitle: "CRM software built for handyman businesses",
    heroSub:
      "Keep every job straight, quote in seconds, schedule your week, and get paid the day you finish.",
    intro:
      "A handyman business lives on volume — lots of small jobs, lots of customers, lots of little payments. FieldKrew keeps it all organized without the bloat: capture the request, send a quick quote, schedule it, and collect payment on the spot, so you spend your evenings resting instead of doing paperwork.",
    pains: [
      {
        title: "Too many small jobs to track in your head",
        body: "Five jobs across town today, eight tomorrow. Without a system, something gets missed and a customer gets annoyed.",
      },
      {
        title: "Quoting needs to take seconds, not minutes",
        body: "For a $200 job you can't spend 20 minutes on an estimate — but it should still look professional.",
      },
      {
        title: "Getting paid on the spot",
        body: "Chasing $150 checks isn't worth your time. Tap, send, paid.",
      },
    ],
    faqs: [
      {
        q: "Is FieldKrew simple enough for a solo handyman?",
        a: "Yes — that's exactly who it's built for. No bloat, no training; you'll be running jobs from your phone in minutes.",
      },
      {
        q: "Can I send a quick quote and invoice?",
        a: "Yes. Build a quote in seconds, send it for online acceptance, and turn it into a pay link the customer can settle by card immediately.",
      },
      {
        q: "Is there a free plan?",
        a: "Yes — free for up to 5 leads and 3 active jobs, which is enough to try it on real work before upgrading to $49.99/month unlimited.",
      },
      {
        q: "Do I need to download an app?",
        a: "No — FieldKrew runs in your browser and installs to your home screen like an app, working offline on the job.",
      },
    ],
  },
  {
    slug: "concrete",
    name: "Concrete",
    noun: "concrete contractors",
    seoTitle: "Concrete CRM Software — Takeoffs, Jobs, Invoices",
    seoDescription:
      "A CRM built for concrete contractors: quote by the yard, schedule pours, document the pour with photos and invoice when the job is signed off.",
    heroTitle: "CRM software built for concrete contractors",
    heroSub:
      "Quote driveways, patios, and foundations, schedule pours around the weather, document the work, and collect deposits.",
    intro:
      "Concrete work is weather-dependent, deposit-driven, and unforgiving on scheduling — a missed pour window costs real money. FieldKrew helps concrete contractors quote flatwork and structural jobs fast, collect a deposit to lock the job, schedule around the forecast, and document every pour with photos.",
    pains: [
      {
        title: "Weather blows up your schedule",
        body: "A rained-out pour cascades through your whole week. You need to reschedule fast and keep customers informed.",
      },
      {
        title: "Deposits lock in the material order",
        body: "You're fronting concrete and rebar. A deposit protects you from a customer who flakes after you've ordered.",
      },
      {
        title: "Big bids deserve professional quotes",
        body: "A foundation or large patio is a serious bid — a clean, itemized quote wins more of them.",
      },
    ],
    faqs: [
      {
        q: "Can I collect a deposit before ordering materials?",
        a: "Yes. Bill a deposit by card to lock the job before you front the concrete and rebar, then collect the balance on completion.",
      },
      {
        q: "Can I reschedule jobs easily when it rains?",
        a: "Yes — drag jobs on the calendar to reschedule, and keep customer details and quotes attached so nothing's lost in the shuffle.",
      },
      {
        q: "Can I document each pour?",
        a: "Yes. Attach photos to every job for your records and the customer's, and share a branded gallery of the finished work.",
      },
      {
        q: "What does concrete CRM software cost?",
        a: "Free to start, $49.99/month for unlimited — no per-crew fees and far cheaper than enterprise options.",
      },
    ],
  },
  {
    slug: "roofing",
    name: "Roofing",
    noun: "roofers",
    seoTitle: "Roofing CRM Software — Quotes, Deposits & Photos",
    seoDescription:
      "A CRM built for roofing contractors: quote by the square, collect a deposit before the crew rolls, document tear-off and install, get paid on completion.",
    heroTitle: "CRM software built for roofing contractors",
    heroSub:
      "Quote roofs by the square, collect deposits before the crew rolls, document every tear-off + install with photos, and get paid on completion — all from your phone.",
    intro:
      "Roofing runs on tight material math + big deposit checks. A $12,000 tear-off has zero margin for a missed line item, a lost supplier price, or an unbilled change order. FieldKrew keeps every square, every pitch upcharge, every rotten-deck sheet, and every photo tied to the specific job — so when it's time to invoice, nothing gets left off.",
    pains: [
      {
        title: "Quotes get lost between the ladder and the truck",
        body: "A homeowner asks for a number, you jot it in your phone, and by the time you're home the price is off by 8 squares. Retail is $600/sq — that's $4,800 you just gave away.",
      },
      {
        title: "Deposits get chased for weeks",
        body: "You told the customer the crew rolls when the deposit clears. Four days later the shingles are on order and no one's actually sent the ACH.",
      },
      {
        title: "Photo docs vanish when insurance calls",
        body: "The adjuster wants tear-off photos from the north slope on August 12th. They're in your camera roll, your foreman's phone, and a WhatsApp thread. Finding them costs half a day.",
      },
      {
        title: "Rotten-deck upcharges become fights",
        body: "You quoted 20 sheets max. Crew opened it up and there's 32. You either eat the difference or have an awkward conversation with a homeowner who thought the price was locked.",
      },
    ],
    faqs: [
      {
        q: "Can I quote by the square + auto-add pitch and access upcharges?",
        a: "Yes. Build shingle quotes by material line + labor + pitch % + optional access surcharge. Duplicate a previous quote for a new address in two taps — accessories don't change, only the square count.",
      },
      {
        q: "Does FieldKrew handle deposit collection before crew rolls?",
        a: "Yes. Any quote you send doubles as a payment link — customer can pay 50% deposit right from the quote email/text. Job status doesn't advance to Scheduled until the deposit posts.",
      },
      {
        q: "Where do tear-off + install photos live?",
        a: "Attached to the job. Every photo your crew takes on the FieldKrew app tags automatically to that job's timeline — searchable by date, phase (tear-off / dry-in / install), and photographer. Share a per-job gallery URL with the insurance adjuster in one tap.",
      },
      {
        q: "Can I track subcontractor COI + W-9 for the guys who do the actual tear-off?",
        a: "Yes. Every sub has a profile with COI upload, 30-day expiry warnings, and a magic-link portal they use to confirm the job (no login). Ships a 1099 vendor sync into QuickBooks at year-end too.",
      },
      {
        q: "How much does it cost for a solo roofer?",
        a: "Free forever — unlimited quotes, jobs, invoices, customer messaging. Card payments run at Stripe's 2.9% + $0.30 plus a 1% FieldKrew fee on Free (0% on Pro). Pro is $49.99/mo flat when you hire crew and want AI SMS replies, live crew map, and sub bidding.",
      },
      {
        q: "How does FieldKrew compare to Jobber for roofing?",
        a: "Jobber charges $29-49/mo for the Core plan (1 user) then $29/user beyond that; Grow plan runs $199-399/mo. FieldKrew is $49.99/mo flat with 10 team seats included and 0% platform fee on invoices vs Jobber's 2.9% card fees. A roofer invoicing $10k/mo saves about $250/mo on FieldKrew.",
      },
      {
        q: "How does FieldKrew compare to Housecall Pro for roofing?",
        a: "Housecall Pro Basic is $59-79/mo for 1 user; Essentials $149-189/mo for 5 users. FieldKrew is $49.99/mo flat with 10 users. HCP is polished and mature; FieldKrew is newer, cheaper, and more focused on project-style trades like roofing rather than one-day service calls.",
      },
      {
        q: "How does FieldKrew compare to AccuLynx or JobNimbus?",
        a: "AccuLynx and JobNimbus are roofing-specific and premium-priced ($99-299/mo per user typically). FieldKrew is generalist but covers the roofing workflow (square-based quotes, deposit collection, tear-off photos, sub COI tracking) at $49.99/mo flat. If you need supplement writing and Xactimate integration, use AccuLynx; if you want a general contractor CRM that handles roofing well, FieldKrew.",
      },
      {
        q: "Can I import my existing customer list from another CRM?",
        a: "Yes. FieldKrew imports CSV exports from Jobber, Housecall Pro, JobNimbus, AccuLynx, and QuickBooks. Fields map automatically — name, phone, email, address, job history. Takes 5 minutes for a 500-customer list.",
      },
      {
        q: "Does FieldKrew sync with QuickBooks Online?",
        a: "Yes. Two-way sync: customers, invoices, and payments flow both directions. FieldKrew is on Intuit's app store. Handles sales tax mapping, class tracking, and 1099 vendor sync for subcontractors.",
      },
      {
        q: "How long does it take to switch from Jobber to FieldKrew?",
        a: "Most roofers finish the switch in one weekend: CSV export from Jobber Friday night, upload to FieldKrew, sanity-check on Saturday, cut over Monday. Historical jobs come along as read-only; new jobs go through FieldKrew. Chris (our first roofer) migrated 40 customers in about 90 minutes.",
      },
      {
        q: "Is there a mobile app for iPhone and Android?",
        a: "Yes. Native iOS app is on the App Store; Android app is in beta (Play Store submission Q4 2026). Both run the full CRM — quotes, jobs, photos, invoices, messaging — from the field. Web app also works on any phone browser.",
      },
      {
        q: "Does FieldKrew work offline on job sites without cell signal?",
        a: "The iOS app has offline-first mode: quotes, notes, and photos all queue locally and sync when signal returns. Web + Android are network-dependent. Full offline sync on Android is on the roadmap.",
      },
      {
        q: "Can I collect insurance restoration deposits?",
        a: "Yes. Split-pay invoices handle insurance work: ACV check upfront, RCV depreciation released after completion, homeowner deductible collected via card link. Each portion tracked separately, invoice shows the split.",
      },
      {
        q: "Can I send digital contracts for roofing jobs?",
        a: "Yes. Any quote can be sent as an e-signable contract — customer signs on their phone, timestamped and stored against the job. Includes IP address + user agent for legal record. No DocuSign fees.",
      },
      {
        q: "How do I request Google reviews after a completed roof?",
        a: "FieldKrew's Reviews module sends an SMS/email to the customer 24-48 hours after job completion with a one-tap link to your Google review page. Included in Pro. Contractors typically see 40-60% response rate vs 5-10% on manual requests.",
      },
      {
        q: "Does FieldKrew handle change orders for extra deck sheets or upgrades?",
        a: "Yes. Change orders live inside the job — add scope, price, and customer signature. Original invoice can regenerate to include change orders, or invoice them separately. Prevents the awkward 'we added 12 sheets but forgot to tell you' end-of-job conversation.",
      },
      {
        q: "What card processing rate does FieldKrew charge for roofing deposits?",
        a: "Stripe's standard 2.9% + $0.30 for cards. On top of that: 1% platform fee on Free plan, 0% on Pro. ACH bank transfers run 0.8% capped at $5 — cheaper for large deposits. A $6,000 deposit via ACH costs $5; via card costs $174.",
      },
      {
        q: "Can I run FieldKrew as a franchise or multi-location roofing company?",
        a: "Yes. Locations feature lets you segment jobs, invoices, and reports by office while keeping one master account. Each location has its own crew, its own P&L, and its own customer portal domain if you want it. Rolls up to a single company-wide dashboard.",
      },
      {
        q: "Does FieldKrew send automatic follow-ups on quotes that haven't been accepted?",
        a: "Yes. Follow-up automations ping stale quotes at 3, 7, and 14 days after send. Text or email. Customizable per contractor. Recovers roughly 15-25% of otherwise-dead quotes based on early user data.",
      },
      {
        q: "Is my data safe if FieldKrew shuts down?",
        a: "Data export is one click — full CSV dump of every customer, job, quote, invoice, and payment. No lock-in. Backups run daily to Cloudflare R2 with 30-day retention. If FieldKrew disappeared tomorrow, you'd have your data.",
      },
    ],
    workflow: [
      { stage: "Lead intake", what: "Homeowner submits a roof-quote request from your website form, calls, or texts. Auto-creates a lead with photos they've attached — no manual data entry." },
      { stage: "On-site quote", what: "Measure with EagleView / drone / by hand, punch square count + pitch + accessories into the mobile quote. Sign on the tablet before you leave the driveway." },
      { stage: "Deposit collection", what: "Quote email doubles as a pay link. Customer pays 50% deposit by card or ACH; job status auto-advances to Scheduled only after deposit clears." },
      { stage: "Material order", what: "Job details include square count, shingle line + color, accessories. Copy directly into your supplier's order form. Deliver-to address auto-fills from the job." },
      { stage: "Crew day", what: "Crew opens the job on their phone, sees the material list + special notes (skylights? satellite dish?). Snaps tear-off / dry-in / install photos that auto-tag to the job." },
      { stage: "Change orders", what: "Rotten deck sheet count exceeds the quoted 20? Add a change order in one tap, get customer signature on their phone, add to final invoice." },
      { stage: "Final invoice", what: "Job marked complete → invoice auto-generates with deposit deducted, balance due. Sent by SMS + email with pay link. Insurance job? Split invoice into ACV / RCV / deductible portions." },
      { stage: "Review request", what: "24-48h after payment, an automated SMS goes out asking for a Google review with a one-tap link. Typical 40-60% response rate." },
    ],
    services: [
      { name: "Full residential re-roof", detail: "Tear-off, new decking as needed, ice-and-water shield, underlayment, architectural shingles, ridge vent, flashing, gutters optional.", range: "$8k-25k" },
      { name: "Storm / insurance restoration", detail: "Adjuster inspection, supplement writing, ACV/RCV split invoicing, deductible collection from homeowner, photo documentation for adjuster.", range: "$10k-40k" },
      { name: "Metal roof install", detail: "Standing seam Galvalume / aluminum, snap-lock or mechanical seam, snow guards, matching flashings. Typically 24-gauge for residential.", range: "$18k-55k" },
      { name: "Flat roof (TPO / EPDM / modified bitumen)", detail: "Commercial low-slope work, tear-off or overlay, membrane roll installation, edge metal, drains.", range: "$5-12/sq ft" },
      { name: "Repair / leak trace", detail: "Diagnostic visit, replace 20-100 sq ft of shingles, reflash a chimney or pipe boot, seal a leak. Often a stepping-stone to full re-roof.", range: "$300-2,500" },
      { name: "Skylight replacement", detail: "Remove existing Velux/Wasco unit, install new fixed or vented skylight with ice-and-water shield + flashing kit.", range: "$800-2,500/unit" },
      { name: "Gutter install", detail: "6-inch K-style seamless aluminum, guards optional, downspout extensions. Often bundled with re-roof.", range: "$8-15/lin ft" },
      { name: "Emergency tarp", detail: "Post-storm tarping to prevent interior damage until insurance approves the full scope. Frequently no-charge if the customer signs the restoration contract.", range: "$300-1,200" },
    ],
    seasonalTips: [
      "Peak season runs March-October in most of the US; storm season (April-July for hail states, June-November for hurricane states) is when a small operator can 3x annual revenue in 60 days if they're set up to respond fast.",
      "Winter downtime is when your best crews leave. Recurring inspection contracts, gutter cleaning packages, and insurance-supplement writing keep them paid year-round.",
      "Insurance restoration doubles a small roofer's ticket size — but requires COI documentation, W-9s on file, and photo trails that will hold up in a supplement dispute. FieldKrew's photo timeline solves the last one.",
      "Hail-storm chasers show up the moment a storm hits. If your customer list has a per-neighborhood tag, you can send targeted 'we're already in your neighborhood' outreach within 24 hours of the storm and beat the chasers on trust.",
      "Referrals are the entire game — 60-80% of established roofers' work comes from prior customers. Automated review requests + a searchable customer archive with photos are how you turn one satisfied job into three next spring.",
    ],
  },
  {
    slug: "pest-control",
    name: "Pest control",
    noun: "pest control technicians",
    seoTitle: "Pest Control CRM — Recurring Routes & Invoicing",
    seoDescription:
      "A CRM built for pest control operators: schedule recurring treatments, track each property's history, bill the route and chase nothing by hand.",
    heroTitle: "CRM software built for pest control operators",
    heroSub:
      "Auto-schedule quarterly + monthly recurring services, run route-optimized tech days, log every product application to the job, and get paid on completion — from your phone.",
    intro:
      "Pest control is a recurring-revenue business or it isn't a business. FieldKrew's recurring-jobs engine spawns your quarterly + monthly + annual contracts on schedule, keeps every product application logged to the specific customer for state reporting, and auto-invoices when the tech marks the job complete. Cancellations become the exception, not the leak.",
    pains: [
      {
        title: "Recurring services get manually rescheduled",
        body: "You've got 180 customers on quarterly. Every Sunday you spend 90 min rebuilding next week's schedule from memory + a spreadsheet.",
      },
      {
        title: "Product application logs live on paper",
        body: "State inspector shows up asking for last quarter's Termidor SC applications by customer. You've got a stack of clipboards. Half a day gone.",
      },
      {
        title: "Route inefficiency eats margin",
        body: "Techs zigzag across town because Monday's schedule was built by whoever asked most recently. Two extra stops per day = 30% more truck fuel + one less job per tech per week.",
      },
      {
        title: "Renewal reminders never fire",
        body: "Annual termite bond expires. Customer doesn't renew because no one reminded them. You lose $400 in ARR per missed renewal.",
      },
    ],
    faqs: [
      {
        q: "Does FieldKrew handle recurring quarterly / bi-monthly / annual service contracts?",
        a: "Yes. Set a customer on a cadence (30, 60, 90 days, custom) and jobs auto-spawn on that schedule with the standard price + tech assigned. Auto-invoices on completion. Cancel or pause anytime.",
      },
      {
        q: "Can I log EPA-registered product applications for state compliance?",
        a: "Yes. Each job has a Products Applied section — capture product name, EPA reg number, target pest, quantity, area treated, and applicator name. Export as CSV for state inspector requests.",
      },
      {
        q: "Is there route optimization for tech days?",
        a: "The calendar view lets you drag stops into a coherent day; auto-order-by-address bunches nearby jobs. Full turn-by-turn route optimization is on the roadmap — for now, address clustering handles ~90% of the routing pain.",
      },
      {
        q: "Can I set up automated termite bond renewal reminders?",
        a: "Yes. Every recurring contract can have a renewal date and auto-email/SMS reminder cadence (60 days, 30 days, 7 days out). Customer clicks the link to renew, contract auto-extends.",
      },
      {
        q: "How much for a 2-tech pest control company?",
        a: "Free works for solo. Pro at $49.99/mo flat is right the moment you hire the first tech — 10 team seats included, so your 2-tech shop pays the same as a 10-tech shop.",
      },
      {
        q: "How does FieldKrew compare to PestPac for pest control?",
        a: "PestPac (WorkWave) is enterprise-priced, typically $150-500+/mo per user with per-tech charges. FieldKrew is $49.99/mo flat for up to 10 techs. PestPac has deeper route optimization + state-report integrations; FieldKrew covers recurring contracts, EPA product logs, and card payments at a fraction of the cost.",
      },
      {
        q: "How does FieldKrew compare to FieldRoutes or ServSuite?",
        a: "FieldRoutes and ServSuite are dedicated pest control platforms with turn-by-turn routing and state DPR/DACS integrations. FieldKrew is a general contractor CRM adapted for pest control — cheaper ($49.99/mo vs $99-249/mo) and simpler, but skinnier on pest-specific routing. Best fit for 1-5 tech shops; scale beyond that and you outgrow it.",
      },
      {
        q: "Can I import my customer list from PestPac or ServiceCEO?",
        a: "Yes. CSV export → CSV upload; FieldKrew maps customer records, addresses, service history, and contract cadence. A 500-customer PestPac account typically imports in under 15 minutes.",
      },
      {
        q: "Does FieldKrew log EPA reg numbers for state inspector audits?",
        a: "Yes. Products Applied section captures product name, EPA registration number, target pest, quantity, area treated, and applicator name per job. Filter by date range and export as CSV for California DPR, Florida DACS, or any state's regulator on request.",
      },
      {
        q: "Can I send WDO / termite inspection reports?",
        a: "Yes. Form Builder handles WDO reports — customizable per state (Florida NPMA-33, California Structural Pest Control Board 43M, etc.). Send as PDF, e-sign by inspector + customer, archive against the job.",
      },
      {
        q: "Does FieldKrew handle annual termite bond renewals with auto-billing?",
        a: "Yes. Bond renewals live as recurring annual jobs with auto-invoicing 60 / 30 / 7 days before expiry. Auto-charges the card on file when the bond renews.",
      },
      {
        q: "Is there a mobile app for pest techs in the field?",
        a: "Native iOS app is on the App Store; Android in beta (Play Store Q4 2026). Techs log chemistry, take before/after photos, mark jobs complete, and trigger auto-invoicing from their phone.",
      },
      {
        q: "Does FieldKrew sync with QuickBooks for pest control?",
        a: "Yes. Customers, invoices, and payments sync both ways. Also handles class tracking (residential vs commercial vs termite bond revenue) and 1099 vendor sync for subcontracted work.",
      },
      {
        q: "How do I request Google reviews after a pest treatment?",
        a: "Reviews module sends an SMS/email 24-48 hours after job completion with a one-tap review link. 40-60% response rates typical, vs 5-10% for manual requests.",
      },
      {
        q: "Can I offer online booking for one-time pest services?",
        a: "Yes. Public Booking page — customer picks service type (ant treatment, wasp removal, one-time general), date, and pays deposit online. Auto-creates the job on your calendar.",
      },
      {
        q: "How long does it take to switch from PestPac to FieldKrew?",
        a: "Most shops move over in one weekend: CSV export Friday, upload + spot-check Saturday, cut over Monday. New jobs go through FieldKrew immediately; PestPac stays available read-only until you cancel.",
      },
      {
        q: "Does FieldKrew work offline for techs in rural service areas?",
        a: "iOS app queues chemistry logs, photos, and job notes locally when offline; syncs on reconnect. Android + web are network-dependent for now.",
      },
      {
        q: "Can commercial accounts see their service history in a customer portal?",
        a: "Yes. Toggle per-account: commercial customers get a login-free portal showing every visit, product applied, and invoice. Useful for facility managers who need to prove pest control compliance to their tenants.",
      },
      {
        q: "Does FieldKrew handle multi-location pest control franchises?",
        a: "Yes. Locations feature segments jobs, techs, revenue, and P&L per branch office while rolling up to a company-wide dashboard.",
      },
      {
        q: "Is my data safe? Can I export everything if I leave?",
        a: "One-click CSV export of every customer, job, chemistry log, and invoice. No lock-in. Daily backups to Cloudflare R2, 30-day retention.",
      },
    ],
    workflow: [
      { stage: "Contract signup", what: "New customer selects quarterly / bi-monthly / monthly service tier online or via phone; e-signs the service agreement with terms + cancellation policy." },
      { stage: "Initial visit + baseline", what: "First treatment establishes baseline: perimeter treatment, interior spray, product line + rate applied. Photos of every entry point taken and tagged to the customer." },
      { stage: "Recurring auto-schedule", what: "Recurring engine spawns the next service on cadence (30/60/90 days) and auto-assigns to the tech who covers that route. No Sunday-night schedule rebuild." },
      { stage: "Route day", what: "Tech opens their calendar, sees 8-15 stops clustered by address. Each stop shows treatment history + special notes (pet locations, kids' play areas, no-spray zones)." },
      { stage: "Product application log", what: "At each stop tech logs product (Termidor SC), EPA reg number, quantity, target pest, area treated — all against the customer for state-inspector audit trail." },
      { stage: "Renewal + upsell", what: "Termite bond renewals auto-remind at 60/30/7 days. WDO inspection requests come in via portal. Add-on services (mosquito, rodent) upsold seasonally." },
      { stage: "Auto-invoice + payment", what: "Job complete → invoice auto-generates → autopay charges card on file. Recurring customers rarely see an invoice email; it's fully hands-off." },
    ],
    services: [
      { name: "General pest quarterly", detail: "Interior + exterior treatment for ants, spiders, roaches, silverfish. Perimeter barrier + interior spot treatment.", range: "$85-150/visit" },
      { name: "Termite bond (annual)", detail: "Annual inspection, retreatment if needed, warranty against structural termite damage. High-margin recurring revenue.", range: "$300-800/yr" },
      { name: "Termite pretreatment (new construction)", detail: "Soil treatment before slab pour, per-square-foot pricing. Volume work for pest ops with builder contracts.", range: "$0.85-1.50/sq ft" },
      { name: "Rodent exclusion", detail: "Seal entry points, set snap traps + tamper-proof bait stations, quarterly monitoring. Popular add-on to general service.", range: "$400-1,500 initial + $50-100/quarter" },
      { name: "Mosquito treatment (seasonal)", detail: "Monthly barrier spray April-October, larvicide in standing water. Add-on to general pest for outdoor-lifestyle customers.", range: "$70-120/visit" },
      { name: "Bed bug treatment", detail: "Heat or chemical protocol, multiple visits, often insurance/legal-involved. High-ticket one-time work.", range: "$800-3,500" },
      { name: "WDO / termite inspection", detail: "Real-estate transaction inspection with state-required report (NPMA-33, CA 43M, etc.). Fast turnaround, low-labor work.", range: "$85-150" },
      { name: "Wildlife removal", detail: "Squirrels, raccoons, opossums — trap + relocate + exclusion. Often bundled with rodent exclusion.", range: "$300-1,500" },
    ],
    seasonalTips: [
      "Termite swarm season (March-May in most of the US) is your peak lead-gen window; email your customer list a 'termite awareness' note 2 weeks before your region's typical swarm date to capture inspection requests early.",
      "Mosquito season drives 20-30% of add-on revenue for many general pest ops; a spring email upsell to existing customers converts 15-25% into a mosquito plan.",
      "Rodent exclusion demand spikes in October-November as mice move indoors for winter; run a targeted 'winter rodent prep' campaign 30 days before your first frost date.",
      "Real-estate WDO/termite inspections spike May-August with the summer moving season; build direct relationships with 5-10 realtors in your area and you'll have referral flow year-round.",
      "Regulatory compliance varies wildly by state: California requires DPR licensing + Structural Pest Control Board reporting, Florida DACS is separate again. FieldKrew's product application log covers the raw data any state wants, but your state-specific reporting workflow is on you.",
    ],
  },
  {
    slug: "cleaning",
    name: "Cleaning",
    noun: "cleaning contractors",
    seoTitle: "Cleaning CRM Software — Recurring Jobs & Invoices",
    seoDescription:
      "A CRM built for cleaning crews: schedule recurring visits, assign the crew, document the work with photos and invoice the route automatically.",
    heroTitle: "CRM software built for cleaning contractors",
    heroSub:
      "Auto-schedule weekly + bi-weekly + monthly recurring cleans, dispatch the right crew to each property, run per-home checklists, and collect payment when the job's done — from your phone.",
    intro:
      "Cleaning is a recurring, predictable business — or it should be. FieldKrew keeps every recurring house + office on its own cadence, auto-spawns the job in the crew's calendar, runs a per-property checklist so nothing's missed (or forgotten by a new crew member), and auto-invoices on completion. Retention comes from the crew showing up on time and the invoice hitting before the customer forgets you were there.",
    pains: [
      {
        title: "Recurring cleans get rebuilt every week by hand",
        body: "60 customers on weekly + 40 on bi-weekly. Every Sunday you rebuild the week from a paper calendar + a spreadsheet. 2 hours you'll never get back.",
      },
      {
        title: "New crew members miss stuff at properties they haven't been to",
        body: "Regular crew is out sick. Backup crew doesn't know Mrs. Chen wants baseboards done monthly, not weekly. Complaint on Monday, discount to keep the account.",
      },
      {
        title: "Invoices go out days after the clean",
        body: "You wait until Friday to invoice a Tuesday clean. Customer's already forgotten you were there. 43-day average pay time instead of 15.",
      },
      {
        title: "Add-on services (deep clean, move-out) never get billed",
        body: "Customer asks for oven cleaning during a regular clean. Crew does it. No one adds it to the invoice. Free labor, every month.",
      },
    ],
    faqs: [
      {
        q: "Does FieldKrew handle weekly / bi-weekly / monthly recurring cleans?",
        a: "Yes. Set each customer on a cadence and jobs auto-spawn in your calendar. Change the schedule for one week (customer's on vacation) without breaking the cadence. Auto-invoices on completion.",
      },
      {
        q: "Can I attach a checklist to each property so any crew member does it the same way?",
        a: "Yes. Each customer has a per-property notes section — surface it as a checklist the crew sees on their phone when they open the job. Backup crew does it right the first time.",
      },
      {
        q: "How do add-on services (deep clean, oven, windows) get billed?",
        a: "Crew adds the add-on to the job on the app, sets the price, done. It shows on the auto-generated invoice the next morning. Nothing gets missed.",
      },
      {
        q: "Does FieldKrew handle commercial contracts (janitorial), not just residential?",
        a: "Yes. Same recurring engine — set a nightly / weekly office contract with monthly billing. Commercial customer gets a login-free customer portal to see completed cleans + service log.",
      },
      {
        q: "How much for a 5-person cleaning crew?",
        a: "$49.99/mo Pro flat, 10 team seats included, so 5 crew = you have 5 spare seats for growth. Card payments at Stripe's 2.9% + $0.30, 0% FieldKrew markup on Pro.",
      },
      {
        q: "How does FieldKrew compare to Jobber for cleaning?",
        a: "Jobber Core is $29-49/mo (1 user) then $29/user; recurring jobs are limited to their Grow tier ($199-399/mo). FieldKrew ships full recurring at $49.99/mo flat for 10 users. A 5-person cleaning shop pays roughly 4x more on Jobber for equivalent capability.",
      },
      {
        q: "How does FieldKrew compare to Housecall Pro for cleaning?",
        a: "HCP Essentials is $149-189/mo for 5 users. FieldKrew is $49.99/mo for 10 users with equivalent scheduling, recurring, and payment features. HCP has a mature dispatcher view; FieldKrew's is simpler but faster on mobile.",
      },
      {
        q: "How does FieldKrew compare to ZenMaid or Launch27?",
        a: "ZenMaid and Launch27 are cleaning-specific ($49-119/mo). FieldKrew is generalist at $49.99/mo. If you need cleaning-industry integrations (Broom & Duster staffing, cleaning-specific booking flows), ZenMaid wins. If you want a general CRM that handles cleaning workflows well, FieldKrew matches on features and price.",
      },
      {
        q: "Does FieldKrew handle both residential (weekly cleans) and commercial (janitorial) contracts?",
        a: "Yes. Same recurring engine — set a nightly office contract with monthly billing, or a weekly house clean with per-visit billing. Filter reports by commercial vs residential.",
      },
      {
        q: "Can I offer online booking for one-time deep cleans or move-out cleans?",
        a: "Yes. Public Booking page — customer picks service type (recurring / one-time / deep / move-out), square footage or hourly, date, and pays deposit online. Auto-creates the job on your calendar.",
      },
      {
        q: "Can I import my customer list from ZenMaid, Launch27, or a spreadsheet?",
        a: "Yes. CSV import handles customer name, address, service cadence (weekly / bi-weekly / monthly), price per visit, and last-visit date. A 200-customer ZenMaid list imports in under 10 minutes.",
      },
      {
        q: "Does FieldKrew sync with QuickBooks for cleaning?",
        a: "Yes. Customers, invoices, payments both directions. Handles class tracking (residential vs commercial revenue) and 1099 sync for subcontracted cleaners.",
      },
      {
        q: "How does crew clock-in and payroll tracking work?",
        a: "Crew members clock in from their phone at each property; time is tracked per job and rolls up into a weekly timesheet CSV you can hand to your payroll provider. Also feeds job costing so you see true profit per clean.",
      },
      {
        q: "Can I dispatch different crews to different properties automatically?",
        a: "Yes. Assign a preferred crew to each customer; recurring jobs auto-assign to that crew. Override for a single week when someone's out sick. Backup crew sees the property checklist so they don't miss Mrs. Chen's baseboards.",
      },
      {
        q: "How do I request Google reviews after a clean?",
        a: "Reviews module sends SMS/email 24-48 hours post-completion with one-tap review link. Cleaning contractors consistently see the highest response rates (60-70%) because clean = visible outcome.",
      },
      {
        q: "Does FieldKrew handle key or garage code storage per property?",
        a: "Yes. Each customer has secure notes (key location, garage code, alarm code, pet warnings) that only assigned crew can see on their phone. Encrypted at rest.",
      },
      {
        q: "Is there an iPhone and Android app for crew members?",
        a: "Native iOS app is on the App Store; Android in beta (Play Store Q4 2026). Crew opens job, sees checklist, marks tasks complete, uploads before/after photos.",
      },
      {
        q: "Can I mark a customer 'no-service' when they're on vacation without deleting them?",
        a: "Yes. Pause any recurring job for a specific week or date range; cadence resumes automatically after. Invoice doesn't generate for paused visits.",
      },
      {
        q: "How long does it take to switch from ZenMaid or Launch27?",
        a: "Most cleaning shops migrate in a weekend: CSV export Friday, upload + sanity-check Saturday, cut over Monday. Historical jobs come as read-only; new jobs run through FieldKrew.",
      },
      {
        q: "Is my data safe? Can I export everything if I leave?",
        a: "One-click CSV export of every customer, recurring schedule, job, and invoice. Daily backups to Cloudflare R2. No lock-in.",
      },
    ],
    workflow: [
      { stage: "Customer signup", what: "Homeowner books via online form or calls; picks recurring frequency (weekly / bi-weekly / monthly) + square footage. E-signs terms of service." },
      { stage: "Walkthrough + baseline", what: "First visit is often a walkthrough to build the per-property checklist: baseboards? oven? which rooms weekly vs monthly? Photos of any pre-existing damage noted." },
      { stage: "Recurring auto-schedule", what: "Recurring engine spawns cleans on the customer's cadence, auto-assigned to their preferred crew. Vacation pauses handled per-week without breaking the cadence." },
      { stage: "Crew day", what: "Crew opens the job, sees the property checklist, key/code location, pet warnings. Clocks in on arrival (time-tracks for payroll + job costing)." },
      { stage: "Add-ons during clean", what: "Customer asks for oven or windows mid-visit; crew adds the line-item at their price on their phone. It shows on tonight's invoice — nothing gets missed." },
      { stage: "Job complete", what: "Crew snaps 3-5 before/after photos, marks tasks done on the checklist, taps Complete. Auto-triggers invoice generation + review request scheduling." },
      { stage: "Auto-invoice + autopay", what: "Invoice hits customer email/SMS within minutes of crew leaving. Autopay customers get charged automatically; non-autopay get a pay link. Median payment time drops from 30-45 days to under 7." },
    ],
    services: [
      { name: "Recurring residential (weekly)", detail: "Kitchen, bathrooms, living areas, bedrooms — standard checklist. 2-hr average visit for 2,000 sq ft home.", range: "$100-180/visit" },
      { name: "Recurring residential (bi-weekly)", detail: "Same scope as weekly, slightly deeper because it's been 14 days. Most-common residential cadence.", range: "$130-220/visit" },
      { name: "Deep clean (one-time)", detail: "Move-in condition: baseboards, cabinet fronts, oven, fridge, blinds, light fixtures. 4-6 hours for 2,000 sq ft.", range: "$300-600" },
      { name: "Move-out clean", detail: "Empty-property deep clean for landlords / real-estate agents. Turnaround-critical work, often billed to landlord.", range: "$250-500" },
      { name: "Post-construction clean", detail: "Fine dust removal after remodel or new build; 6-10 hours typical. Higher rate because of the labor + PPE needed.", range: "$0.30-0.75/sq ft" },
      { name: "Commercial janitorial (nightly)", detail: "Small offices, dental practices, salons. Recurring nightly or 3x/week; monthly billing. Long-term stable revenue.", range: "$0.08-0.18/sq ft/month" },
      { name: "Airbnb turnover", detail: "Same-day turnover with linen swap + restocking. Coordinated via cleaner-portal or Turno integration.", range: "$75-150/turn" },
      { name: "Window cleaning", detail: "Interior + exterior residential, or storefront commercial. Often bundled add-on for existing customers.", range: "$150-500 residential, $50-200 storefront" },
    ],
    seasonalTips: [
      "Spring cleaning demand peaks March-May; a targeted spring-clean email to your recurring customers converts 20-40% into a paid one-time deep clean on top of their normal cadence.",
      "Move-in/move-out cleans spike in June-August with the summer moving season; building 3-5 property-manager relationships gives you steady turnover work year-round.",
      "Holiday season (November-December) drives one-time cleans before hosting — pre-Thanksgiving and pre-Christmas 'guest-ready' packages are proven upsells.",
      "Winter is slower for residential; commercial janitorial and Airbnb turnover fill the gap since offices don't stop needing cleaning in January.",
      "Retention is the whole business — a customer worth $200/mo for 3 years is $7,200 lifetime revenue. Auto-invoicing (kills the 'they forgot to pay' issue) + a per-property checklist (kills the 'the new crew missed stuff' issue) protect that LTV more than any marketing spend.",
    ],
  },
  {
    slug: "pool-service",
    name: "Pool service",
    noun: "pool service technicians",
    seoTitle: "Pool Service CRM — Routes, Chemicals & Invoicing",
    seoDescription:
      "A CRM built for pool service routes: log each stop, track chemicals and equipment per property, and bill the whole route on one monthly run.",
    heroTitle: "CRM software built for pool service operators",
    heroSub:
      "Run route-optimized weekly service days, log every chemistry reading + product added to the specific pool, track equipment repairs, and auto-invoice on completion — from your phone.",
    intro:
      "Pool service is a route business first, a chemistry business second, and an equipment-repair business third. FieldKrew keeps all three in the same job record: every recurring pool on a cadence, a chemistry log per visit tied to that pool's history, and a separate repair-job flow for the times a pump or heater eats an afternoon. Nothing lives in a paper notebook on the truck.",
    pains: [
      {
        title: "Chemistry readings written on a wet clipboard, then lost",
        body: "You're back in the truck, hands full, the clipboard's damp. You'll enter this into the spreadsheet later. You don't.",
      },
      {
        title: "One tech knows the customer's equipment, no one else does",
        body: "Regular tech is out. Backup tech doesn't know the Kramer pool's Hayward pump has been noisy for 3 weeks. Misses the sign, pump fails, customer's Fourth of July party is dry.",
      },
      {
        title: "Repair jobs get treated like service stops",
        body: "You billed a $340 salt cell replacement as part of the weekly $85 service because it wasn't a separate job. You just donated $340.",
      },
      {
        title: "Route builds are guesswork",
        body: "Monday route was built by memory. You realize on Tuesday you drove past 3 pools that were supposed to be Monday. 40 minutes of gas wasted.",
      },
    ],
    faqs: [
      {
        q: "Does FieldKrew have a per-pool chemistry log?",
        a: "Yes. Each pool (job) has a Chemistry section — record FC, TC, CC, pH, TA, CH, CYA, temp, chemicals added, and notes on every visit. History tail shows the last N visits so trends are obvious.",
      },
      {
        q: "How do equipment repairs get separated from routine service?",
        a: "Convert a service stop into a repair job in one tap — different job type, different price, different invoice line. Routine service continues on cadence; the repair invoices separately.",
      },
      {
        q: "Can I run route-optimized weekly service days?",
        a: "The calendar clusters jobs by address so a Monday route with 12 pools bunches by neighborhood. Full turn-by-turn optimization is on the roadmap; address clustering + drag-to-reorder handles most of the routing pain today.",
      },
      {
        q: "Does the customer see the chemistry log?",
        a: "Optional. Toggle per-account: customers can log into a portal that shows their last 12 chemistry readings + any equipment notes. Some pool guys treat this as a Pro-tier upsell ($10-15/mo more).",
      },
      {
        q: "How much for a 3-tech pool service company?",
        a: "$49.99/mo Pro flat, 10 team seats included. You could add 7 more techs before paying a cent extra per seat.",
      },
      {
        q: "How does FieldKrew compare to Skimmer for pool service?",
        a: "Skimmer is the pool-specific market leader at $49-99/mo. FieldKrew is $49.99/mo flat with equivalent chemistry logs + recurring routes, plus a full CRM (quotes, jobs, invoices, subs) around it. If you only want route + chemistry, Skimmer is more polished; if you want an all-in-one, FieldKrew matches on price and adds the rest.",
      },
      {
        q: "How does FieldKrew compare to PoolTrac or Pooltrackr?",
        a: "Both are pool-specific at $30-99/mo. Trade-off same as Skimmer — they're deeper on pool workflow, FieldKrew is broader on business management. Most single-truck operators pick whichever they saw a friend using; multi-truck ops tend to appreciate FieldKrew's team + payments features.",
      },
      {
        q: "Can I import my customer list from Skimmer or PoolTrac?",
        a: "Yes. CSV export from either → CSV upload to FieldKrew. Customer name, address, pool volume, current chemistry baseline, service cadence, and price all map over. Typical 300-pool route imports in ~15 minutes.",
      },
      {
        q: "Does the chemistry log alert on out-of-range readings?",
        a: "Yes. Set target ranges per pool (some homeowners run high CYA, some low); readings that fall outside get flagged red on the log so the tech knows to add chemicals or note an issue before leaving.",
      },
      {
        q: "Can I offer online booking for one-time pool openings or closings?",
        a: "Yes. Seasonal one-time services (spring opening, fall closing, green-to-clean) all get their own booking form. Customer picks a date, pays deposit, auto-scheduled to your calendar.",
      },
      {
        q: "How does auto-invoicing work for weekly pool service?",
        a: "When the tech marks a job complete, an invoice auto-generates with the recurring price + any chemicals added + any repair work. Emailed/texted to customer within minutes of the tech leaving. Cuts average pay time from 30-45 days to 7-14.",
      },
      {
        q: "Can I bill customers on autopay so I'm not chasing 100 weekly invoices?",
        a: "Yes. Set customers on autopay (card or ACH); FieldKrew charges the card on file when the invoice generates. Card 2.9% + $0.30, ACH 0.8% capped at $5. For weekly $85 service that's $2.77 card or $0.68 ACH.",
      },
      {
        q: "Does FieldKrew sync with QuickBooks for pool service?",
        a: "Yes. Customers, invoices, payments both directions. Handles class tracking (service vs repair vs product sales) and 1099 sync for helpers.",
      },
      {
        q: "How do I request Google reviews after pool service?",
        a: "Reviews module sends SMS/email 24-48 hours after job completion with one-tap review link. Pool service response rates typically 40-55%.",
      },
      {
        q: "Is there a mobile app for techs?",
        a: "Native iOS on App Store; Android in beta (Play Store Q4 2026). Tech logs chemistry, takes pool photos, marks complete, triggers invoice — all from their phone in the customer's backyard.",
      },
      {
        q: "Does FieldKrew work offline for techs at pools without cell signal?",
        a: "iOS app queues chemistry logs and photos when offline; syncs when signal returns. Android and web are network-dependent for now.",
      },
      {
        q: "How do I handle equipment repairs (pump, heater, salt cell)?",
        a: "Convert a service stop into a repair job in one tap; different job type, different price, different invoice line. The routine weekly service continues on cadence; the repair invoices separately so a $340 salt cell doesn't get accidentally rolled into $85 service.",
      },
      {
        q: "Can I track pool equipment models per property for future repairs?",
        a: "Yes. Each customer has an Equipment section — pump make/model, heater, filter, sanitizer type. Populates automatically into repair quotes so you're not asking the customer 'what pump do you have?' six months later.",
      },
      {
        q: "How long does it take to switch from Skimmer to FieldKrew?",
        a: "Most pool guys switch over in one weekend: CSV export Friday, upload Saturday, cut over Monday. Skimmer's chemistry history imports as CSV; historical readings stay searchable in FieldKrew from day one.",
      },
      {
        q: "Is my data safe? Can I export everything if I leave?",
        a: "One-click CSV export of every customer, chemistry log, repair, and invoice. Daily R2 backups, 30-day retention. No lock-in.",
      },
    ],
    workflow: [
      { stage: "Customer signup", what: "Homeowner books via website form or referral; picks weekly / bi-weekly cadence + pool volume. E-signs service agreement." },
      { stage: "Baseline visit", what: "First visit documents pool volume, current chemistry, equipment inventory (pump, heater, filter, sanitizer type), pre-existing wear on tile, coping, decking." },
      { stage: "Route auto-schedule", what: "Recurring engine spawns pool visits on cadence, auto-assigned to the tech covering that neighborhood. Calendar clusters by address so Monday's 12 pools stay in one geographic loop." },
      { stage: "Chemistry log per visit", what: "Tech records FC, TC, CC, pH, TA, CH, CYA, temp, chemicals added, and equipment observations. History tail shows previous 12 visits so trends are obvious." },
      { stage: "Repair separation", what: "Failed pump or torn filter? Convert the service stop into a repair job in one tap — different job type, different price, separate invoice. Routine service continues on cadence." },
      { stage: "Auto-invoice + autopay", what: "Job marked complete → invoice auto-generates with recurring price + chemicals added + any repair line-items → autopay charges card on file." },
      { stage: "Customer portal (optional)", what: "Homeowners see their chemistry log + equipment notes anytime via portal. Some pool guys upsell this as a $10-15/mo premium tier." },
    ],
    services: [
      { name: "Weekly chemistry-only service", detail: "Test + balance chemistry, empty skimmer, backwash if needed. 15-20 min per pool. High-density route work.", range: "$65-100/week" },
      { name: "Weekly full service", detail: "Chemistry + brush walls + vacuum + empty baskets + equipment check. 25-40 min per pool. Standard residential.", range: "$80-140/week" },
      { name: "Spring opening", detail: "Pump off winter cover, reassemble equipment, prime pump, chemistry rebalance, filter check. Half to full day.", range: "$300-600" },
      { name: "Fall closing / winterization", detail: "Drain to below skimmer, blow lines, add anti-freeze, install cover. Half day per pool.", range: "$275-550" },
      { name: "Green-to-clean (algae recovery)", detail: "Shock treatment, filter run-time optimization, manual vacuum, chemistry rebuild over 3-7 days. High-margin one-time work.", range: "$400-1,200" },
      { name: "Salt cell replacement", detail: "Remove + replace saltwater generator cell (Hayward AquaRite, Pentair IntelliChlor). 30 min labor + part.", range: "$450-1,100" },
      { name: "Pump replacement", detail: "Diagnose failed pump, install new (Pentair, Hayward, Jandy variable-speed). 1-3 hour job + part.", range: "$800-2,400" },
      { name: "Heater install / repair", detail: "Diagnose or install gas/electric/heat-pump. Larger job, permit sometimes required.", range: "$500-4,500" },
      { name: "Tile / equipment cleaning", detail: "Bead blast tile, acid wash plaster, deep-clean cartridges. Off-season revenue.", range: "$300-1,500" },
    ],
    seasonalTips: [
      "Opening season (March-May in most of the US) is your peak revenue window; a spring email to last year's customers with a locked-in opening price captures ~70% of them before they call around.",
      "Fall closings drive Q4 revenue when weekly service ends; bundle closing + winterization + cover installation into a single-price package to lift ticket size.",
      "Repair work smooths cash flow through the off-season (heater installs, cartridge cleans, plaster resurfacing) — build customer equipment records now so you know which pools need which repair when.",
      "Green-to-cleans are the highest-margin one-time work in pool service — a customer who let their pool go over vacation pays $400-1,200 for what's 4-6 hours of your time.",
      "Retention beats acquisition: a $85/week customer for 5 years is $22k lifetime revenue. Auto-invoicing (kills the 'they forgot to pay' issue) + chemistry alerts (kills the 'my pool turned green' complaint) protect that far better than any marketing budget.",
    ],
  },
];

export const TRADE_INDEX = new Map(TRADES.map((t) => [t.slug, t]));
